import { computed, DestroyRef, effect, inject, Injector, Service, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription, take } from 'rxjs';
import { AuthenticationService } from '../authentication/authentication.service';
import { SharedBillingInvitationComponent } from '../shared/dialogs/shared-billing-invitation/shared-billing-invitation.component';
import { BillingMembershipDto, MceApiService } from './mceapi.client.generated';

@Service()
export class SharedBillingInvitationService {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly api = inject(MceApiService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly routeEvent = toSignal(this.router.events, { initialValue: null });
  private readonly readyToPresent = computed(() => {
    // router.url and router.navigated are updated by router events, rather than signals.
    this.routeEvent();
    const navigating = this.router.currentNavigation();
    if (!this.authenticationService.currentUserMatchesSignedInAccount()
      || !this.authenticationService.currentUserProfileComplete()
      || this.authenticationService.isExpired()
      || !this.router.navigated || navigating) return false;
    const paths = this.router.parseUrl(this.router.url).root.children['primary']?.segments.map(segment => segment.path) ?? [];
    return paths[0] !== 'authentication' && !(paths[0] === 'profile' && paths[1] === 'edit');
  });
  private started = false;
  private userUid: string | undefined;
  private generation = 0;
  private checked = false;
  private readonly pending = signal<BillingMembershipDto | undefined>(undefined);
  private lookup: Subscription | undefined;
  private dialogRef: MatDialogRef<SharedBillingInvitationComponent> | undefined;

  readonly lookupError = signal(false);

  start(): void {
    if (this.started) return;
    this.started = true;
    effect(() => {
      const uid = this.authenticationService.user()?.uid;
      const ready = this.readyToPresent();
      this.pending();
      // Opening/closing dialogs and starting HTTP requests are imperative side effects.
      untracked(() => this.synchronize(uid, ready));
    }, { injector: this.injector });
    this.destroyRef.onDestroy(() => this.reset());
  }

  retry(): void {
    if (!this.lookupError() || !this.readyToPresent()) return;
    this.checked = false;
    this.lookupError.set(false);
    this.synchronize(this.authenticationService.user()?.uid, true);
  }

  private isCurrentSession(uid: string, generation: number): boolean {
    return generation === this.generation && this.authenticationService.user()?.uid === uid;
  }

  private synchronize(uid: string | undefined, ready: boolean): void {
    if (uid !== this.userUid) {
      this.reset();
      this.userUid = uid;
    }
    if (!uid || !ready) return;
    const invitation = this.pending();
    if (invitation && !this.dialogRef) {
      this.present(invitation, uid, this.generation);
    } else if (!this.checked) {
      this.check(uid, this.generation);
    }
  }

  private check(uid: string, generation: number): void {
    this.checked = true;
    this.lookup = this.api.v1SharedBillingInvitationsGet().subscribe({
      next: invitations => {
        if (!this.isCurrentSession(uid, generation)) return;
        const invitation = invitations.find(item => item.status === 'pending');
        if (invitation && !invitation.uid) {
          this.lookupError.set(true);
          return;
        }
        // Retain the invitation if profile data is temporarily unavailable; show it once ready.
        this.pending.set(invitation);
      },
      error: () => {
        if (this.isCurrentSession(uid, generation)) this.lookupError.set(true);
      },
    });
  }

  private present(invitation: BillingMembershipDto, uid: string, generation: number): void {
    const ref = this.dialog.open(SharedBillingInvitationComponent, {
      width: '480px',
      maxWidth: '95vw',
      closeOnNavigation: false,
      ariaLabelledBy: 'shared-billing-invitation-title',
      ariaDescribedBy: 'shared-billing-invitation-description',
      data: {
        invitationUid: invitation.uid!,
        isCurrentSession: () => this.isCurrentSession(uid, generation),
      },
    });
    this.dialogRef = ref;
    ref.afterClosed().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (generation !== this.generation) return;
      this.dialogRef = undefined;
      this.pending.set(undefined);
    });
  }

  private reset(): void {
    this.generation++;
    this.lookup?.unsubscribe();
    this.lookup = undefined;
    this.dialogRef?.close();
    this.dialogRef = undefined;
    this.pending.set(undefined);
    this.checked = false;
    this.lookupError.set(false);
  }
}
